/**
 * IconAction (DNKADM-0418) — an icon-only button that says what it does.
 *
 * One `label` is both the tooltip (MUI `Tooltip`, shown on hover and on keyboard
 * focus) and the button's accessible name (`aria-label`), so an owner hovering a
 * bare glyph and a screen-reader user hear the same words. Every icon-only action in
 * dink-admin renders through this instead of a per-page `Tooltip` + `IconButton`
 * pair; `src/iconAction.guard.test.ts` fails a raw unlabeled `IconButton` under the
 * Plans & Packages features.
 *
 * The button always sits inside an inline-flex span, and the span is the Tooltip's
 * child. A disabled button fires no pointer events, so the span is what takes the
 * hover (MUI's documented wrapper; `AppLayout` and `PaymentMethodsScreen` already
 * used it). It is there in both states so flipping `disabled` never remounts the
 * button: the element keeps its identity and keyboard focus. `describeChild` keeps
 * the accessible NAME on the button only: without it MUI copies the title onto the
 * span as a second `aria-label`. With it MUI treats the tooltip as a description
 * instead, so the span carries a native `title` while the tooltip is closed and
 * `aria-describedby` while it is open (MUI 9.1.1); the span never gets an
 * `aria-label`.
 */
import { Box, IconButton, Tooltip, type IconButtonProps } from '@mui/material';

export interface IconActionProps extends Omit<IconButtonProps, 'aria-label' | 'title'> {
  /**
   * What the action does, e.g. `Edit package Starter`. Rendered as the tooltip text
   * and as the button's `aria-label`.
   */
  label: string;
}

export function IconAction({ label, ...rest }: IconActionProps) {
  return (
    <Tooltip title={label} describeChild>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <IconButton aria-label={label} {...rest} />
      </Box>
    </Tooltip>
  );
}
